import { useState, useEffect, useMemo, useRef, type KeyboardEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import { IS_LOCAL_DEMO } from "@/components/demo/DemoPackageSwitch";
import {
  ArrowLeft,
  Save,
  Truck,
  MapPin,
  Package,
  Clock,
  Weight,
  StickyNote,
  ChevronDown,
  Plus,
  Search,
  Loader2,
  Trash2,
} from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────────────────

type RecentAddresses = {
  destinations: Array<{
    address: string;
    city: string;
    state: string;
    zip: string;
    contact: string;
  }>;
  carriers: Array<{
    name: string;
    driver: string;
    truck_number: string;
    license: string;
    trailer: string;
  }>;
  customers: string[];
  clientTags?: string[];
};

type OpsClient = {
  id: number;
  name: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  notes: string | null;
};

type CustomerOption = {
  name: string;
  source?: string;
  sources?: string[];
  clientId?: number | null;
  contact?: string | null;
  phone?: string | null;
  subtitle?: string | null;
  destination?: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  } | null;
};

type CargoMode = "loose" | "flatbed";

type CargoLineItem = {
  key: string;
  quantity: string;
  unit: "pallet" | "tote" | "bag" | "equipment" | "other";
  product: string;
  notes: string;
};

type OpsProduct = {
  id: number;
  product_name: string;
  product_id: string | null;
};

type BolDetail = {
  id: number;
  bol_number: string;
  date: string;
  customer_name: string;
  reference_number: string;
  origin_name: string;
  origin_address: string;
  origin_city: string;
  origin_state: string;
  origin_zip: string;
  destination_address: string;
  destination_city: string;
  destination_state: string;
  destination_zip: string;
  destination_contact: string;
  material_type: string;
  material_description: string;
  load_type: string;
  client_tag: string;
  cargo_mode?: string | null;
  line_items?: Array<{
    quantity?: number;
    unit?: string;
    product?: string;
    notes?: string | null;
  }> | null;
  time_in: string;
  time_out: string;
  scale_operator: string;
  weight_gross: number | null;
  weight_tare: number | null;
  weight_net: number | null;
  weight_tons: number | null;
  carrier_name: string;
  driver_name: string;
  truck_number: string;
  license_plate: string;
  trailer_number: string;
  notes: string;
};

// ── Constants ──────────────────────────────────────────────────────────────────

const FALLBACK_MATERIAL_TYPES = IS_LOCAL_DEMO ? [
  "Compost", "Green Waste", "Wood Chips", "Food Scraps", "Mulch", "Topsoil", "Custom Blend", "Other",
] : [
  "Orchard Compost (Pistachio Blend)",
  "Orchard Compost (Citrus Blend)",
  "Compost",
  "Dairy Compost",
  "Biochar Blend",
  "Topsoil",
  "Potting Soil - Premium Mix",
  "Worm Castings",
  "Mulch - Hardwood",
  "Biosolids",
  "Food Waste",
  "Green Waste",
  "Manure",
  "Wood Chips",
  "Palletized Product",
  "Bagged Product",
  "Equipment/Items",
  "Custom Blend",
  "Other",
];

const LOAD_TYPES = ["Outbound", "Inbound", "Transfer", "Return"];

const CARGO_UNITS: Array<{ value: CargoLineItem["unit"]; label: string }> = [
  { value: "pallet", label: "Pallet" },
  { value: "tote", label: "Tote" },
  { value: "bag", label: "Bag" },
  { value: "equipment", label: "Equipment" },
  { value: "other", label: "Other" },
];

const FLATBED_QUICK_ADD = [
  { label: "PlantPal pallet", product: "PlantPal", unit: "pallet" as const, quantity: "1" },
  { label: "Biochar tote", product: "Biochar Blend", unit: "tote" as const, quantity: "1" },
  { label: "Bagged product", product: "Bagged Product", unit: "bag" as const, quantity: "1" },
  { label: "Equipment", product: "Equipment", unit: "equipment" as const, quantity: "1" },
];

function newLineItem(partial?: Partial<CargoLineItem>): CargoLineItem {
  return {
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    quantity: partial?.quantity ?? "1",
    unit: partial?.unit ?? "pallet",
    product: partial?.product ?? "",
    notes: partial?.notes ?? "",
  };
}

const ORIGIN_LOCATIONS = IS_LOCAL_DEMO ? [
  { name: "Green Valley Demo Yard", address: "100 Demonstration Lane", city: "Congress", state: "AZ", zip: "85332" },
] : [
  {
    name: "Congress AZ Plant",
    address: "18980 Stanton Road",
    city: "Congress",
    state: "AZ",
    zip: "85332",
  },
  {
    name: "Phoenix AZ Facility",
    address: "1634 North 19th Avenue",
    city: "Phoenix",
    state: "AZ",
    zip: "85007",
  },
];

const CLIENT_TAGS = IS_LOCAL_DEMO ? [
  { value: "", label: "None" },
  { value: "Nursery deliveries", label: "Nursery deliveries" },
  { value: "Community garden", label: "Community garden" },
] : [
  { value: "", label: "None" },
  { value: "Vanguard Renewables", label: "Vanguard Renewables" },
  { value: "Willcox Pistachio Project", label: "Willcox Pistachio Project" },
  { value: "3LAG / Jack Mendoza", label: "3LAG / Jack Mendoza" },
  { value: "Flower of the Gods", label: "Flower of the Gods" },
];

const SSW_CARRIER_PRESET = IS_LOCAL_DEMO ? {
  name: "Demo Yard Fleet", driver: "Jamie Lee", truck_number: "DEMO-01", license: "", trailer: "Walking floor",
} : {
  name: "AGAVE/SOIL SEED & WATER",
  driver: "COY COOPER",
  truck_number: "1768",
  license: "AFA10L",
  trailer: "WALKING FLOOR",
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function calcNet(gross: string, tare: string): { net: number | null; tons: number | null } {
  const g = parseFloat(gross);
  const t = parseFloat(tare);
  if (isNaN(g) || isNaN(t)) return { net: null, tons: null };
  const net = g - t;
  return { net, tons: parseFloat((net / 2000).toFixed(4)) };
}

function sameName(a: string, b: string) {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

// Shared input/select styles
const inputClass =
  "w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500 transition-colors";

const labelClass = "block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5";

// ── Autocomplete Component ─────────────────────────────────────────────────────

function AutocompleteInput({
  value,
  onChange,
  suggestions,
  placeholder,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  suggestions: string[];
  placeholder: string;
  label: string;
}) {
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!value.trim()) return suggestions.slice(0, 8);
    const q = value.toLowerCase();
    return suggestions.filter((s) => s.toLowerCase().includes(q)).slice(0, 8);
  }, [value, suggestions]);

  const showDropdown = focused && filtered.length > 0;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setFocused(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <label className={labelClass}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        placeholder={placeholder}
        className={inputClass}
      />
      {showDropdown && (
        <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto py-1">
          {filtered.map((item, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                onChange(item);
                setFocused(false);
              }}
              className="block w-full text-left px-3 py-2 text-sm text-foreground hover:bg-secondary transition-colors truncate"
            >
              {item}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function highlightMatch(text: string, query: string) {
  const q = query.trim();
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="font-semibold text-foreground">{text.slice(idx, idx + q.length)}</span>
      {text.slice(idx + q.length)}
    </>
  );
}

function ProductSearchInput({
  value,
  onChange,
  suggestions,
  placeholder,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  suggestions: string[];
  placeholder: string;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    const ranked = suggestions
      .map((name) => {
        const lower = name.toLowerCase();
        let score = 0;
        if (!q) score = lower === "plantpal" ? 10 : 1;
        else if (lower === q) score = 100;
        else if (lower.startsWith(q)) score = 80;
        else if (lower.includes(q)) score = 50;
        else return null;
        return { name, score };
      })
      .filter((row): row is { name: string; score: number } => !!row)
      .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name))
      .slice(0, 10)
      .map((row) => row.name);
    return ranked;
  }, [suggestions, value]);

  const exactMatch = filtered.some((name) => name.toLowerCase() === value.trim().toLowerCase());
  const showCustom = value.trim().length > 0 && !exactMatch;
  const options = showCustom ? [`Use “${value.trim()}”`, ...filtered] : filtered;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [value, open]);

  function choose(name: string) {
    const picked = name.startsWith("Use “") ? value.trim() : name;
    onChange(picked);
    setOpen(false);
    inputRef.current?.blur();
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(options.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (options[activeIndex]) choose(options[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={ref} className="relative">
      <label className={labelClass}>{label}</label>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className={`${inputClass} pl-9 pr-9`}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
        />
        <ChevronDown
          size={14}
          className={`absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none transition-transform ${open ? "rotate-180" : ""}`}
        />
      </div>
      {open && (
        <div className="absolute z-40 left-0 right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-xl overflow-hidden">
          <div className="max-h-64 overflow-y-auto py-1">
            {options.length === 0 ? (
              <p className="px-3 py-3 text-sm text-muted-foreground">
                No catalog match. Keep typing a custom product name.
              </p>
            ) : (
              options.map((name, i) => {
                const isCustom = showCustom && i === 0;
                return (
                  <button
                    key={`${name}-${i}`}
                    type="button"
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => choose(name)}
                    className={`flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors ${
                      i === activeIndex ? "bg-secondary" : "hover:bg-secondary/70"
                    }`}
                  >
                    <div className="min-w-0">
                      <p className={`text-sm truncate ${isCustom ? "text-muted-foreground italic" : "text-foreground"}`}>
                        {isCustom ? name : highlightMatch(name, value)}
                      </p>
                    </div>
                    {!isCustom && (
                      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                        Catalog
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function sourceLabel(option: CustomerOption) {
  const sources = option.sources?.length ? option.sources : option.source ? [option.source] : [];
  if (sources.includes("bol")) return "Prior BOL";
  if (sources.includes("client")) return "Client";
  if (sources.includes("sales")) return "Sales";
  if (sources.includes("lead")) return "Lead";
  return "Match";
}

function CustomerSearchInput({
  value,
  onChange,
  onSelect,
  token,
  placeholder,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  onSelect: (option: CustomerOption) => void;
  token: string | null;
  placeholder: string;
  label: string;
}) {
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState(value);
  const [options, setOptions] = useState<CustomerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const skipNextSearch = useRef(false);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setFocused(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (!token || !focused) return;
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      setLoading(true);
      try {
        const res = await apiFetch(
          `/api/ops/bols/customer-search?q=${encodeURIComponent(query.trim())}&limit=12`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error("search failed");
        const data = await res.json();
        if (!cancelled) setOptions(data.customers || []);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, query.trim() ? 180 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [focused, query, token]);

  const showDropdown = focused && (loading || options.length > 0 || query.trim().length > 0);

  return (
    <div ref={ref} className="relative">
      <label className={labelClass}>{label}</label>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
          }}
          onFocus={() => setFocused(true)}
          placeholder={placeholder}
          className={`${inputClass} pl-9 pr-9`}
          autoComplete="off"
        />
        {loading && (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />
        )}
      </div>
      {showDropdown && (
        <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-72 overflow-y-auto py-1">
          {loading && options.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">Searching customers…</p>
          ) : options.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              No matches. Keep typing a new customer name.
            </p>
          ) : (
            options.map((option, i) => (
              <button
                key={`${option.name}-${i}`}
                type="button"
                onClick={() => {
                  skipNextSearch.current = true;
                  setQuery(option.name);
                  onSelect(option);
                  setFocused(false);
                }}
                className="block w-full text-left px-3 py-2.5 hover:bg-secondary transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{option.name}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {option.destination?.address
                        ? [option.destination.address, option.destination.city, option.destination.state]
                            .filter(Boolean)
                            .join(", ")
                        : option.subtitle || option.contact || "No saved destination yet"}
                    </p>
                  </div>
                  <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                    {sourceLabel(option)}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function CreateBol() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get("duplicate");

  // ── Form State ──

  const [date, setDate] = useState(todayStr());
  const [customerName, setCustomerName] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");

  // Origin
  const [originName, setOriginName] = useState(ORIGIN_LOCATIONS[0].name);
  const [originAddress, setOriginAddress] = useState(ORIGIN_LOCATIONS[0].address);
  const [originCity, setOriginCity] = useState(ORIGIN_LOCATIONS[0].city);
  const [originState, setOriginState] = useState(ORIGIN_LOCATIONS[0].state);
  const [originZip, setOriginZip] = useState(ORIGIN_LOCATIONS[0].zip);

  // Destination
  const [destAddress, setDestAddress] = useState("");
  const [destCity, setDestCity] = useState("");
  const [destState, setDestState] = useState("");
  const [destZip, setDestZip] = useState("");
  const [destContact, setDestContact] = useState("");

  // Material
  const [materialType, setMaterialType] = useState(FALLBACK_MATERIAL_TYPES[0]);
  const [loadType, setLoadType] = useState(LOAD_TYPES[0]);
  const [clientTag, setClientTag] = useState("");
  const [materialDescription, setMaterialDescription] = useState("");
  const [cargoMode, setCargoMode] = useState<CargoMode>("loose");
  const [lineItems, setLineItems] = useState<CargoLineItem[]>([newLineItem({ product: "PlantPal" })]);

  // Timing
  const [timeIn, setTimeIn] = useState("");
  const [timeOut, setTimeOut] = useState("");
  const [scaleOperator, setScaleOperator] = useState("");

  // Weight
  const [weightGross, setWeightGross] = useState("");
  const [weightTare, setWeightTare] = useState("");

  // Carrier
  const [carrierName, setCarrierName] = useState(SSW_CARRIER_PRESET.name);
  const [driverName, setDriverName] = useState(SSW_CARRIER_PRESET.driver);
  const [truckNumber, setTruckNumber] = useState(SSW_CARRIER_PRESET.truck_number);
  const [licensePlate, setLicensePlate] = useState(SSW_CARRIER_PRESET.license);
  const [trailerNumber, setTrailerNumber] = useState(SSW_CARRIER_PRESET.trailer);

  // Inline client creation
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientContact, setNewClientContact] = useState("");
  const [newClientEmail, setNewClientEmail] = useState("");
  const [newClientPhone, setNewClientPhone] = useState("");
  const [newClientAddress, setNewClientAddress] = useState("");

  // Notes
  const [notes, setNotes] = useState("");

  // ── Derived ──

  const { net: weightNet, tons: weightTons } = calcNet(weightGross, weightTare);

  // ── Fetch recent addresses for autocomplete ──

  const { data: recentData } = useQuery<RecentAddresses>({
    queryKey: ["bol-recent-addresses"],
    queryFn: async () => {
      const res = await apiFetch("/api/ops/bols/recent-addresses", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return { destinations: [], carriers: [], customers: [] };
      return res.json();
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });

  const { data: clients = [], refetch: refetchClients } = useQuery<OpsClient[]>({
    queryKey: ["ops-clients-for-bol"],
    queryFn: async () => {
      const res = await apiFetch("/api/ops/clients", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.clients ?? data ?? [];
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });

  const { data: products = [] } = useQuery<OpsProduct[]>({
    queryKey: ["ops-products-for-bol"],
    queryFn: async () => {
      const res = await apiFetch("/api/ops/products", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.products ?? data ?? [];
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });

  const materialTypes = useMemo(() => {
    const productNames = products.map((p) => p.product_name).filter(Boolean);
    return [...new Set([...productNames, ...FALLBACK_MATERIAL_TYPES])].sort((a, b) => a.localeCompare(b));
  }, [products]);

  const flatbedProductSuggestions = useMemo(() => {
    return [...new Set([...materialTypes, "PlantPal", "Equipment", "Bagged Product"])].sort((a, b) =>
      a.localeCompare(b)
    );
  }, [materialTypes]);

  const carrierSuggestions = useMemo(
    () => [...new Set((recentData?.carriers ?? []).map((c) => c.name).filter(Boolean))],
    [recentData]
  );

  const clientTagOptions = useMemo(() => {
    const values = [
      ...CLIENT_TAGS.map((ct) => ct.value).filter(Boolean),
      ...clients.map((c) => c.name).filter(Boolean),
      ...(recentData?.clientTags ?? []),
      ...(clientTag ? [clientTag] : []),
    ];
    return [
      { value: "", label: "None" },
      ...[...new Set(values)].sort().map((value) => ({ value, label: value })),
    ];
  }, [clients, recentData, clientTag]);

  const customerIsKnown = clients.some((c) => sameName(c.name, customerName));

  function updateCustomerName(value: string) {
    setCustomerName(value);
    const client = clients.find((c) => sameName(c.name, value));
    if (client) setClientTag(client.name);
  }

  function selectCustomer(option: CustomerOption) {
    setCustomerName(option.name);
    setClientTag(option.name);
    if (option.contact) setDestContact(option.contact);
    if (option.destination && (option.destination.address || option.destination.city)) {
      setDestAddress(option.destination.address || "");
      setDestCity(option.destination.city || "");
      setDestState(option.destination.state || "");
      setDestZip(option.destination.zip || "");
    }
  }

  useEffect(() => {
    if (clientTag || !customerName) return;
    const client = clients.find((c) => sameName(c.name, customerName));
    if (client) setClientTag(client.name);
  }, [clientTag, clients, customerName]);

  // ── Duplicate mode: pre-fill form ──

  const { data: duplicateBol } = useQuery<BolDetail>({
    queryKey: ["bol-detail", duplicateId],
    queryFn: async () => {
      const res = await apiFetch(`/api/ops/bols/${duplicateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load BOL for duplication");
      const data = await res.json();
      return data.bol || data;
    },
    enabled: !!token && !!duplicateId,
  });

  useEffect(() => {
    if (!duplicateBol) return;
    const b = duplicateBol;
    setDate(todayStr()); // New date for the duplicate
    setCustomerName(b.customer_name || "");
    setReferenceNumber(b.reference_number || "");
    setOriginName((IS_LOCAL_DEMO ? (b as any).origin_location : null) || b.origin_name || ORIGIN_LOCATIONS[0].name);
    setOriginAddress(b.origin_address || "");
    setOriginCity(b.origin_city || "");
    setOriginState(b.origin_state || "");
    setOriginZip(b.origin_zip || "");
    setDestAddress(b.destination_address || "");
    setDestCity(b.destination_city || "");
    setDestState(b.destination_state || "");
    setDestZip(b.destination_zip || "");
    setDestContact(b.destination_contact || "");
    setMaterialType(b.material_type || FALLBACK_MATERIAL_TYPES[0]);
    setLoadType(b.load_type || LOAD_TYPES[0]);
    setClientTag(b.client_tag || "");
    setMaterialDescription(b.material_description || "");
    const mode = String(b.cargo_mode || "").toLowerCase() === "flatbed" ? "flatbed" : "loose";
    setCargoMode(mode);
    if (mode === "flatbed" && Array.isArray(b.line_items) && b.line_items.length) {
      setLineItems(
        b.line_items.map((item) =>
          newLineItem({
            quantity: String(item.quantity ?? 1),
            unit: (["pallet", "tote", "bag", "equipment", "other"].includes(String(item.unit))
              ? item.unit
              : "pallet") as CargoLineItem["unit"],
            product: item.product || "",
            notes: item.notes || "",
          })
        )
      );
    } else if (mode === "flatbed") {
      setLineItems([newLineItem({ product: "PlantPal" })]);
    }
    setTimeIn(b.time_in || "");
    setTimeOut(b.time_out || "");
    setScaleOperator(b.scale_operator || "");
    setWeightGross(IS_LOCAL_DEMO && (b as any).gross_weight != null ? String((b as any).gross_weight) : b.weight_gross != null ? String(b.weight_gross) : "");
    setWeightTare(IS_LOCAL_DEMO && (b as any).tare_weight != null ? String((b as any).tare_weight) : b.weight_tare != null ? String(b.weight_tare) : "");
    setCarrierName(b.carrier_name || "");
    setDriverName(b.driver_name || "");
    setTruckNumber(b.truck_number || "");
    setLicensePlate(b.license_plate || "");
    setTrailerNumber(b.trailer_number || "");
    setNotes(b.notes || "");
  }, [duplicateBol]);

  // ── Submit ──

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (cargoMode === "flatbed") {
        const valid = lineItems.filter((item) => item.product.trim() && Number(item.quantity) > 0);
        if (!valid.length) {
          throw new Error("Add at least one flatbed line item (pallet, tote, equipment, etc.).");
        }
      }

      const payload = {
        date,
        customerName,
        clientId: clients.find((client) => client.name.trim().toLowerCase() === customerName.trim().toLowerCase())?.id ?? null,
        referenceNumber: referenceNumber || null,
        originLocation: originName,
        originAddress,
        originCity,
        originState,
        originZip,
        destinationAddress: destAddress,
        destinationCity: destCity,
        destinationState: destState,
        destinationZip: destZip,
        onsiteContactName: destContact || null,
        cargoMode,
        lineItems:
          cargoMode === "flatbed"
            ? lineItems.map((item) => ({
                quantity: Number(item.quantity) || 0,
                unit: item.unit,
                product: item.product.trim(),
                notes: item.notes.trim() || null,
              }))
            : [],
        materialType: cargoMode === "loose" ? materialType : "Flatbed Load",
        materialDescription: materialDescription || null,
        loadType,
        clientTag: clientTag || null,
        timeIn: timeIn || null,
        timeOut: timeOut || null,
        scaleOperatorInitials: scaleOperator || null,
        hasWeight: cargoMode === "loose" ? undefined : Boolean(weightGross && weightTare),
        grossWeight: weightGross ? parseFloat(weightGross) : null,
        tareWeight: weightTare ? parseFloat(weightTare) : null,
        netWeight: weightNet,
        netWeightTons: weightTons,
        carrierName: carrierName || null,
        driverName: driverName || null,
        truckNumber: truckNumber || null,
        licensePlate: licensePlate || null,
        trailerNumber: trailerNumber || null,
        notes: notes || null,
      };

      const res = await apiFetch("/api/ops/bols", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || data.error || `Failed to create BOL (${res.status})`);
      }
      return res.json();
    },
    onSuccess: (created) => {
      navigate(IS_LOCAL_DEMO ? `/app/ops/bols/${created.id}` : "/app/ops");
    },
  });

  const createClientMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch("/api/ops/clients", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: customerName.trim(),
          contactName: newClientContact.trim() || null,
          contactEmail: newClientEmail.trim() || null,
          contactPhone: newClientPhone.trim() || null,
          address: newClientAddress.trim() || null,
          notes: "Created from BOL form",
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || data.error || `Failed to create client (${res.status})`);
      }
      return res.json();
    },
    onSuccess: async (client: OpsClient) => {
      await refetchClients();
      setCustomerName(client.name);
      setClientTag(client.name);
      setShowNewClient(false);
      setNewClientContact("");
      setNewClientEmail("");
      setNewClientPhone("");
      setNewClientAddress("");
    },
  });

  // ── Origin quick select ──

  function selectOrigin(index: number) {
    const loc = ORIGIN_LOCATIONS[index];
    setOriginName(loc.name);
    setOriginAddress(loc.address);
    setOriginCity(loc.city);
    setOriginState(loc.state);
    setOriginZip(loc.zip);
  }

  // ── Destination autocomplete ──

  function selectDestination(dest: RecentAddresses["destinations"][0]) {
    setDestAddress(dest.address);
    setDestCity(dest.city);
    setDestState(dest.state);
    setDestZip(dest.zip);
    setDestContact(dest.contact);
  }

  // ── Carrier autocomplete ──

  function selectCarrier(carrier: RecentAddresses["carriers"][0]) {
    setCarrierName(carrier.name);
    setDriverName(carrier.driver);
    setTruckNumber(carrier.truck_number);
    setLicensePlate(carrier.license);
    setTrailerNumber(carrier.trailer);
  }

  // ── Render ──

  return (
    <div className="max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(IS_LOCAL_DEMO ? "/app/ops/bols" : "/app/ops")}
          className="p-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="heading-display text-2xl text-foreground">
            {duplicateId ? "Duplicate BOL" : "Create New BOL"}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Fill in the shipping document details
          </p>
        </div>
      </div>

      {/* Error */}
      {submitMutation.isError && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-xl p-4">
          <p className="text-sm text-red-700 dark:text-red-400">
            {submitMutation.error instanceof Error ? submitMutation.error.message : "Failed to create BOL"}
          </p>
        </div>
      )}

      {/* Form */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitMutation.mutate();
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ── Left Column ── */}
          <div className="space-y-6">
            {/* Delivery Info */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                <Package size={16} className="text-forest-600 dark:text-forest-400" />
                Delivery Info
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Date *</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>Reference #</label>
                  <input
                    type="text"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    placeholder="PO or reference"
                    className={inputClass}
                  />
                </div>
              </div>

              <CustomerSearchInput
                label="Customer Name *"
                value={customerName}
                onChange={updateCustomerName}
                onSelect={selectCustomer}
                token={token}
                placeholder={IS_LOCAL_DEMO ? "Search demo customers…" : "Search clients, leads, or prior BOL customers…"}
              />

              {customerName.trim() && destAddress && (
                <p className="text-xs text-muted-foreground -mt-2">
                  Destination ready: {[destAddress, destCity, destState, destZip].filter(Boolean).join(", ")}
                </p>
              )}
              {customerName.trim() && !customerIsKnown && (
                <div className="rounded-lg border border-dashed border-border bg-muted/30 p-3 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-muted-foreground">
                      This customer is not in the client table yet.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowNewClient((v) => !v)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-secondary hover:bg-secondary/80 text-foreground"
                    >
                      <Plus size={13} />
                      Add client
                    </button>
                  </div>

                  {showNewClient && (
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={newClientContact}
                        onChange={(e) => setNewClientContact(e.target.value)}
                        placeholder="Contact name"
                        className={inputClass}
                      />
                      <input
                        type="email"
                        value={newClientEmail}
                        onChange={(e) => setNewClientEmail(e.target.value)}
                        placeholder="Contact email"
                        className={inputClass}
                      />
                      <input
                        type="tel"
                        value={newClientPhone}
                        onChange={(e) => setNewClientPhone(e.target.value)}
                        placeholder="Phone"
                        className={inputClass}
                      />
                      <input
                        type="text"
                        value={newClientAddress}
                        onChange={(e) => setNewClientAddress(e.target.value)}
                        placeholder="Address"
                        className={inputClass}
                      />
                      <button
                        type="button"
                        onClick={() => createClientMutation.mutate()}
                        disabled={createClientMutation.isPending || !customerName.trim()}
                        className="col-span-2 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white"
                      >
                        <Plus size={14} />
                        {createClientMutation.isPending ? "Adding..." : "Create client and tag BOL"}
                      </button>
                      {createClientMutation.isError && (
                        <p className="col-span-2 text-xs text-red-600">
                          {createClientMutation.error instanceof Error ? createClientMutation.error.message : "Failed to create client"}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Origin */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                <MapPin size={16} className="text-forest-600 dark:text-forest-400" />
                Origin
              </div>

              <div className="flex flex-wrap gap-2">
                {ORIGIN_LOCATIONS.map((loc, i) => (
                  <button
                    key={loc.name}
                    type="button"
                    onClick={() => selectOrigin(i)}
                    className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-colors ${
                      originName === loc.name
                        ? "bg-forest-600 text-white border-forest-600"
                        : "bg-background text-muted-foreground border-border hover:border-forest-300 hover:text-foreground"
                    }`}
                  >
                    {loc.name}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className={labelClass}>Address</label>
                  <input
                    type="text"
                    value={originAddress}
                    onChange={(e) => setOriginAddress(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>City</label>
                  <input
                    type="text"
                    value={originCity}
                    onChange={(e) => setOriginCity(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>State</label>
                    <input
                      type="text"
                      value={originState}
                      onChange={(e) => setOriginState(e.target.value)}
                      className={inputClass}
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Zip</label>
                    <input
                      type="text"
                      value={originZip}
                      onChange={(e) => setOriginZip(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Destination */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                  <MapPin size={16} className="text-blue-600 dark:text-blue-400" />
                  Destination
                </div>

                {/* Recent destinations dropdown */}
                {(recentData?.destinations?.length ?? 0) > 0 && (
                  <div className="relative group">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-secondary rounded-lg transition-colors"
                    >
                      Recent
                      <ChevronDown size={12} />
                    </button>
                    <div className="hidden group-focus-within:block absolute right-0 top-full mt-1 z-20 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto py-1 min-w-[240px]">
                      {recentData!.destinations.map((dest, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => selectDestination(dest)}
                          className="block w-full text-left px-3 py-2 text-sm text-foreground hover:bg-secondary transition-colors"
                        >
                          <p className="font-medium truncate">{dest.address}</p>
                          <p className="text-xs text-muted-foreground">
                            {[dest.city, dest.state, dest.zip].filter(Boolean).join(", ")}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className={labelClass}>Address</label>
                <input
                  type="text"
                  value={destAddress}
                  onChange={(e) => setDestAddress(e.target.value)}
                  placeholder="Street address"
                  className={inputClass}
                />
              </div>

              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-3">
                  <label className={labelClass}>City</label>
                  <input
                    type="text"
                    value={destCity}
                    onChange={(e) => setDestCity(e.target.value)}
                    placeholder="City"
                    className={inputClass}
                  />
                </div>
                <div className="col-span-1">
                  <label className={labelClass}>State</label>
                  <input
                    type="text"
                    value={destState}
                    onChange={(e) => setDestState(e.target.value)}
                    placeholder="AZ"
                    className={inputClass}
                    maxLength={2}
                  />
                </div>
                <div className="col-span-2">
                  <label className={labelClass}>Zip</label>
                  <input
                    type="text"
                    value={destZip}
                    onChange={(e) => setDestZip(e.target.value)}
                    placeholder="85001"
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Contact Person</label>
                <input
                  type="text"
                  value={destContact}
                  onChange={(e) => setDestContact(e.target.value)}
                  placeholder="Name or phone"
                  className={inputClass}
                />
              </div>
            </section>

            {/* Material */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                  <Package size={16} className="text-amber-600 dark:text-amber-400" />
                  Material
                </div>
                <div className="inline-flex rounded-lg border border-border bg-secondary/60 p-0.5">
                  <button
                    type="button"
                    onClick={() => setCargoMode("loose")}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                      cargoMode === "loose"
                        ? "bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Loose bulk
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCargoMode("flatbed");
                      if (!lineItems.length) setLineItems([newLineItem({ product: "PlantPal" })]);
                      if (/walk/i.test(trailerNumber)) setTrailerNumber("FLATBED");
                    }}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                      cargoMode === "flatbed"
                        ? "bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Flatbed
                  </button>
                </div>
              </div>

              <p className="text-xs text-muted-foreground -mt-1">
                {cargoMode === "loose"
                  ? "Walking floor / dump truck — one bulk material."
                  : "Pallets, totes, bags, or equipment — add line items fast."}
              </p>

              {cargoMode === "loose" ? (
                <div>
                  <label className={labelClass}>Material Type *</label>
                  <select
                    value={materialType}
                    onChange={(e) => setMaterialType(e.target.value)}
                    className={inputClass}
                    required
                  >
                    {materialTypes.map((mt) => (
                      <option key={mt} value={mt}>
                        {mt}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-1.5">
                    {FLATBED_QUICK_ADD.map((preset) => (
                      <button
                        key={preset.label}
                        type="button"
                        onClick={() =>
                          setLineItems((prev) => [
                            ...prev,
                            newLineItem({
                              product: preset.product,
                              unit: preset.unit,
                              quantity: preset.quantity,
                            }),
                          ])
                        }
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-full border border-border bg-secondary/50 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                      >
                        <Plus size={11} />
                        {preset.label}
                      </button>
                    ))}
                  </div>

                  <div className="space-y-2">
                    {lineItems.map((item, index) => (
                      <div
                        key={item.key}
                        className="rounded-lg border border-border bg-background/70 p-2.5 space-y-2"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Line {index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setLineItems((prev) =>
                                prev.length <= 1 ? [newLineItem()] : prev.filter((row) => row.key !== item.key)
                              )
                            }
                            className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                            aria-label="Remove line"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                        <div className="grid grid-cols-[64px_1fr] gap-2">
                          <div>
                            <label className={labelClass}>Qty</label>
                            <input
                              type="number"
                              min="0.01"
                              step="1"
                              value={item.quantity}
                              onChange={(e) =>
                                setLineItems((prev) =>
                                  prev.map((row) =>
                                    row.key === item.key ? { ...row, quantity: e.target.value } : row
                                  )
                                )
                              }
                              className={inputClass}
                            />
                          </div>
                          <div>
                            <label className={labelClass}>Unit</label>
                            <select
                              value={item.unit}
                              onChange={(e) =>
                                setLineItems((prev) =>
                                  prev.map((row) =>
                                    row.key === item.key
                                      ? { ...row, unit: e.target.value as CargoLineItem["unit"] }
                                      : row
                                  )
                                )
                              }
                              className={inputClass}
                            >
                              {CARGO_UNITS.map((unit) => (
                                <option key={unit.value} value={unit.value}>
                                  {unit.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <ProductSearchInput
                          label="Product / item"
                          value={item.product}
                          onChange={(product) =>
                            setLineItems((prev) =>
                              prev.map((row) => (row.key === item.key ? { ...row, product } : row))
                            )
                          }
                          suggestions={flatbedProductSuggestions}
                          placeholder="Search catalog or type a custom name…"
                        />
                        <div>
                          <label className={labelClass}>Line notes</label>
                          <input
                            type="text"
                            value={item.notes}
                            onChange={(e) =>
                              setLineItems((prev) =>
                                prev.map((row) =>
                                  row.key === item.key ? { ...row, notes: e.target.value } : row
                                )
                              )
                            }
                            placeholder="Optional — lot #, condition, etc."
                            className={inputClass}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setLineItems((prev) => [...prev, newLineItem()])}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-forest-700 dark:text-forest-400 hover:underline"
                  >
                    <Plus size={13} />
                    Add line item
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Load Type *</label>
                  <select
                    value={loadType}
                    onChange={(e) => setLoadType(e.target.value)}
                    className={inputClass}
                    required
                  >
                    {LOAD_TYPES.map((lt) => (
                      <option key={lt} value={lt}>
                        {lt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Client Tag</label>
                  <select
                    value={clientTag}
                    onChange={(e) => setClientTag(e.target.value)}
                    className={inputClass}
                  >
                    {clientTagOptions.map((ct) => (
                      <option key={ct.value} value={ct.value}>
                        {ct.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelClass}>
                  {cargoMode === "flatbed" ? "Extra notes" : "Description"}
                </label>
                <textarea
                  value={materialDescription}
                  onChange={(e) => setMaterialDescription(e.target.value)}
                  placeholder={
                    cargoMode === "flatbed"
                      ? "Anything else for this load…"
                      : "Additional material details..."
                  }
                  rows={2}
                  className={`${inputClass} resize-none`}
                />
              </div>
            </section>
          </div>

          {/* ── Right Column ── */}
          <div className="space-y-6">
            {/* Timing */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                <Clock size={16} className="text-forest-600 dark:text-forest-400" />
                Timing
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Time In</label>
                  <input
                    type="time"
                    value={timeIn}
                    onChange={(e) => setTimeIn(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Time Out</label>
                  <input
                    type="time"
                    value={timeOut}
                    onChange={(e) => setTimeOut(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Scale Operator</label>
                <input
                  type="text"
                  value={scaleOperator}
                  onChange={(e) => setScaleOperator(e.target.value)}
                  placeholder="Operator name"
                  className={inputClass}
                />
              </div>
            </section>

            {/* Weight */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                  <Weight size={16} className="text-amber-600 dark:text-amber-400" />
                  Weight
                </div>
                {cargoMode === "flatbed" && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Optional for flatbed
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Gross (lbs)</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={weightGross}
                    onChange={(e) => setWeightGross(e.target.value)}
                    placeholder="0"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Tare (lbs)</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={weightTare}
                    onChange={(e) => setWeightTare(e.target.value)}
                    placeholder="0"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Auto-calculated fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    Net (lbs)
                  </p>
                  <p className="text-lg font-bold text-foreground tabular-nums">
                    {weightNet !== null ? weightNet.toLocaleString() : "--"}
                  </p>
                </div>
                <div className="bg-forest-50 dark:bg-forest-950/30 rounded-lg p-3 border border-forest-200 dark:border-forest-900">
                  <p className="text-xs font-semibold uppercase tracking-wider text-forest-700 dark:text-forest-400 mb-1">
                    Tons
                  </p>
                  <p className="text-lg font-bold text-forest-700 dark:text-forest-400 tabular-nums">
                    {weightTons !== null ? weightTons.toFixed(2) : "--"}
                  </p>
                </div>
              </div>
            </section>

            {/* Carrier */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                  <Truck size={16} className="text-blue-600 dark:text-blue-400" />
                  Carrier
                </div>

                <button
                  type="button"
                  onClick={() => selectCarrier(SSW_CARRIER_PRESET)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-secondary rounded-lg transition-colors"
                >
                  {IS_LOCAL_DEMO ? "Demo Yard Fleet" : "SSW Fleet"}
                </button>

                {/* Recent carriers dropdown */}
                {(recentData?.carriers?.length ?? 0) > 0 && (
                  <div className="relative group">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-secondary rounded-lg transition-colors"
                    >
                      Recent
                      <ChevronDown size={12} />
                    </button>
                    <div className="hidden group-focus-within:block absolute right-0 top-full mt-1 z-20 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto py-1 min-w-[200px]">
                      {recentData!.carriers.map((carrier, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => selectCarrier(carrier)}
                          className="block w-full text-left px-3 py-2 text-sm text-foreground hover:bg-secondary transition-colors"
                        >
                          <p className="font-medium">{carrier.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {[carrier.driver, carrier.truck_number].filter(Boolean).join(" - ")}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <AutocompleteInput
                label="Carrier Name"
                value={carrierName}
                onChange={setCarrierName}
                suggestions={carrierSuggestions}
                placeholder="Trucking company"
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Driver</label>
                  <input
                    type="text"
                    value={driverName}
                    onChange={(e) => setDriverName(e.target.value)}
                    placeholder="Driver name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Truck #</label>
                  <input
                    type="text"
                    value={truckNumber}
                    onChange={(e) => setTruckNumber(e.target.value)}
                    placeholder="Truck number"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>License Plate</label>
                  <input
                    type="text"
                    value={licensePlate}
                    onChange={(e) => setLicensePlate(e.target.value)}
                    placeholder="License plate"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Trailer #</label>
                  <input
                    type="text"
                    value={trailerNumber}
                    onChange={(e) => setTrailerNumber(e.target.value)}
                    placeholder="Trailer number"
                    className={inputClass}
                  />
                </div>
              </div>
            </section>

            {/* Notes */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                <StickyNote size={16} className="text-muted-foreground" />
                Notes
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes, instructions, or special handling..."
                rows={4}
                className={`${inputClass} resize-none`}
              />
            </section>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-between mt-8 pb-8">
          <button
            type="button"
            onClick={() => navigate(IS_LOCAL_DEMO ? "/app/ops/bols" : "/app/ops")}
            className="px-5 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitMutation.isPending || !date || !customerName.trim()}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-forest-600/20"
          >
            <Save size={16} />
            {submitMutation.isPending ? "Saving..." : "Create BOL"}
          </button>
        </div>
      </form>
    </div>
  );
}
